import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { SetSwitch, SetTextarea } from '../SettingsShell/controls';
import { BOOKABLE_LABEL, CATEGORY_COLOUR } from '../../settings-booking/types';
import type { AppointmentReason, ReasonSentence } from '../../settings-booking/types';
import { ROLE_LABEL } from '../../settings-practice/types';
import type { Provider, Role } from '../../settings-practice/types';

export interface AddSentenceModalProps {
  open: boolean;
  reasons: AppointmentReason[];
  holders: Map<Role, Provider[]>;
  /** Pre-selected when opened from the drawer; null from the card or Train ELVA. */
  initialReason: AppointmentReason | null;
  /** Arrives pre-filled from `Change it` on Train ELVA — the cheapest way the list grows. */
  initialText?: string;
  onClose: () => void;
  onAdd: (s: ReasonSentence) => void;
}

/**
 * Add a sentence (spec 15 §8.4). Three fields, and the third does the real work.
 *
 * Validation: the sentence is non-empty and at least three words, a reason is
 * chosen, and if the condition toggle is on the condition is non-empty.
 */
export function AddSentenceModal({
  open,
  reasons,
  holders,
  initialReason,
  initialText,
  onClose,
  onAdd,
}: AddSentenceModalProps) {
  const [text, setText] = useState(initialText ?? '');
  const [reasonId, setReasonId] = useState<string | null>(initialReason?.id ?? null);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [useCondition, setUseCondition] = useState(false);
  const [condition, setCondition] = useState('');

  useEffect(() => {
    if (open) {
      setText(initialText ?? '');
      setReasonId(initialReason?.id ?? null);
      setPicking(initialReason === null);
      setQuery('');
      setUseCondition(false);
      setCondition('');
    }
  }, [open, initialReason, initialText]);

  const chosen = reasons.find((r) => r.id === reasonId) ?? null;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reasons;
    return reasons.filter(
      (r) => r.displayName.toLowerCase().includes(q) || r.code.toLowerCase().includes(q),
    );
  }, [reasons, query]);

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const valid = wordCount >= 3 && chosen !== null && (!useCondition || condition.trim() !== '');

  return (
    <ViewportOverlay open={open} onClose={onClose}>
      <div
        onClick={(e) => { e.stopPropagation(); }}
        className="flex max-h-[90vh] w-[min(640px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
          <div>
            <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">Add a sentence</h2>
            <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-muted)]">
              Something a patient would say, and the reason it means
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="x" size={14} stroke={2} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {/* 1 — what the patient says */}
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            What the patient says
          </div>
          <p className="m-0 mb-2 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            <strong className="text-[var(--text-secondary)]">
              Write it the way a patient would say it, not the way a clinician would.
            </strong>{' '}
            <em>&ldquo;The temporary came off&rdquo;</em>, not <em>&ldquo;provisional restoration debonded&rdquo;</em> —
            the whole point is that it matches the words ELVA actually hears.
          </p>
          <SetTextarea
            ariaLabel="What the patient says"
            rows={2}
            value={text}
            onChange={setText}
            placeholder="The temporary came off but it doesn’t hurt"
          />

          {/* 2 — it means */}
          <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">It means</div>
          {chosen && !picking ? (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span
                className="flex items-center gap-2 rounded-[8px] px-2 py-1.5"
                style={{ background: `${CATEGORY_COLOUR[chosen.category]}14` }}
              >
                <span
                  className="rounded-[5px] px-1.5 py-0.5 font-[var(--font-mono)] text-[9.5px] font-bold"
                  style={{
                    background: `${CATEGORY_COLOUR[chosen.category]}26`,
                    color: CATEGORY_COLOUR[chosen.category],
                  }}
                >
                  {chosen.code}
                </span>
                <span className="text-[12px] font-bold text-[var(--text-primary)]">{chosen.displayName}</span>
              </span>
              <span className="text-[12px] text-[var(--text-muted)]">{reasonMeta(chosen, holders)}</span>
              <button
                type="button"
                onClick={() => { setPicking(true); }}
                className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="mt-2">
              <span className="flex items-center gap-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-2">
                <Icon kind="search" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
                <input
                  value={query}
                  autoFocus
                  onChange={(e) => { setQuery(e.target.value); }}
                  placeholder={`Search the ${String(reasons.length)} reasons…`}
                  aria-label="Search reasons"
                  className="w-full bg-transparent text-[12.5px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
                />
              </span>
              <div className="mt-2 max-h-[220px] overflow-y-auto rounded-[10px] border border-[var(--border-default)]">
                {matches.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setReasonId(r.id);
                      setPicking(false);
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[var(--surface-sunken)]"
                    style={{ borderBottom: '1px solid #edf0f6' }}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="rounded-[5px] px-1.5 py-0.5 font-[var(--font-mono)] text-[9.5px] font-bold"
                        style={{
                          background: `${CATEGORY_COLOUR[r.category]}26`,
                          color: CATEGORY_COLOUR[r.category],
                        }}
                      >
                        {r.code}
                      </span>
                      <span className="truncate text-[12.5px] font-semibold text-[var(--text-primary)]">
                        {r.displayName}
                      </span>
                    </span>
                    {/* Length, role and who may book it — so nobody points patients at a staff-only reason unawares. */}
                    <span className="shrink-0 text-[11px] text-[var(--text-muted)]">{reasonMeta(r, holders)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 3 — only when */}
          <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Only when — optional
          </div>
          <p className="m-0 mb-2 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            Leave it off and the sentence always means this reason.{' '}
            <strong className="text-[var(--text-secondary)]">
              Turn it on when the same words can mean two different things
            </strong>
            , and the difference is something ELVA can look up.
          </p>

          <div className="flex items-center justify-between gap-4 rounded-[10px] bg-[#FBF3E4] px-3.5 py-3">
            <span>
              <span className="block text-[13.5px] font-semibold text-[var(--warn-amber)]">Add a condition</span>
              <span className="block text-[11.5px] text-[var(--warn-amber)]">For words that can mean two things</span>
            </span>
            <SetSwitch label="Add a condition" checked={useCondition} onChange={setUseCondition} />
          </div>

          {useCondition ? (
            <>
              <div className="mt-2">
                <SetTextarea
                  ariaLabel="Only when"
                  rows={2}
                  value={condition}
                  onChange={setCondition}
                  placeholder="there is an open crown prep on the chart"
                />
              </div>
              <p className="m-0 mt-2 rounded-[10px] bg-[var(--surface-sunken)] px-3 py-2.5 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
                <strong>It may refer to what ELVA can actually see:</strong> the chart (open treatment, the last visit, a
                booked appointment), whether the patient is new or existing, the time of day, and the day of the week.{' '}
                <strong>A condition about anything else is ignored rather than guessed at</strong> — ELVA does not know
                whether somebody sounded worried.
              </p>
              {/* The worked example, because nobody reaches for this field unprompted. */}
              <p className="m-0 mt-2 rounded-[10px] bg-[#FBF3E4] px-3 py-2.5 text-[11.5px] leading-[1.6] text-[var(--warn-amber)]">
                <strong>The clearest case is a crown.</strong> <em>&ldquo;My crown fell off&rdquo;</em> is an emergency;{' '}
                <em>&ldquo;the temporary came off&rdquo;</em> is this appointment —{' '}
                <strong>but only when there is an open prep on the chart.</strong> Same subject, same words almost,
                opposite answers. Without the condition the two sentences compete and which one wins is luck.
              </p>
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            Saved against <strong className="text-[var(--text-secondary)]">Maria</strong> and today&rsquo;s date, and{' '}
            <strong className="text-[var(--text-secondary)]">live on the next call.</strong>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!valid}
              onClick={() => {
                if (!valid) return;
                onAdd({
                  id: `s_${String(Date.now())}`,
                  reasonId: chosen.id,
                  text: text.trim(),
                  ...(useCondition ? { condition: condition.trim() } : {}),
                  source: 'written_here',
                  author: 'Maria',
                });
              }}
              className={cn(
                'rounded-[10px] px-4 py-2 text-[13.5px] font-semibold text-white',
                valid ? 'bg-[var(--brand-indigo)]' : 'cursor-not-allowed bg-[var(--brand-indigo)] opacity-40',
              )}
            >
              Add sentence
            </button>
          </span>
        </div>
      </div>
    </ViewportOverlay>
  );
}

/** "45 min · General dentist · ELVA may book it" */
function reasonMeta(r: AppointmentReason, holders: Map<Role, Provider[]>): string {
  const roleNames = r.roles
    .map((role) => `${ROLE_LABEL[role]}${(holders.get(role) ?? []).length === 0 ? ' · nobody' : ''}`)
    .join(', ');
  const book =
    r.bookableBy === 'anyone'
      ? 'anyone may book it'
      : r.bookableBy === 'elva_and_staff'
        ? 'ELVA may book it'
        : `${BOOKABLE_LABEL[r.bookableBy]} — ELVA takes a message`;
  return `${String(r.durationMinutes)} min · ${roleNames} · ${book}`;
}
